import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import TextField from "@/common/elements/InputElements/TextField";
import Select from "@/common/elements/InputElements/Select";
import SecureFormItem from "@/common/SecureFormItem";
import DynamicForm from "@/common/DynamicForm";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import { AliasesList } from "@/components/BookingsDetails/PersonalInformationDetails/AliasesList";
import { AddressesList } from "@/components/BookingsDetails/PersonalInformationDetails/AddressesList";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import {
  getOptionsFromRefList,
  convertApiFields,
  getSQLTypeToHTMLType,
  isNonEmptyVariable,
} from "@/util/common_function";
import {
  fetchExtraTableRecord,
  saveExtraTableRecord,
} from "@/util/extraColumnActions";

// mode: "add" | "modify" | "detail" — "detail" renders the form read-only.
// Exposes validateAndGetPayload() for the parent (InterestedParties.jsx)'s
// OK button — the victim record (including its bio/aliases/addresses) is
// only persisted when the whole Warrant is saved (activeWarrant.
// warrantVictim), same "deferred until the aggregate save" model as
// WorkCrew's Roster/BlackList entries. WarrantVictimExtra is the one
// exception — it's saved immediately here when editing an already-saved
// victim (a real warrantVictimID), same reasoning as every other
// list-child extra table in this app: a brand new victim only gets that
// id once the whole Warrant is saved, which this modal doesn't wait for.
const WarrantVictimForm = forwardRef(
  ({ initialValues = null, disabled = false, startingPoint = "Warrants" }, ref) => {
    const [form] = Form.useForm();
    const [aliases, setAliases] = useState([]);
    const [addresses, setAddresses] = useState([]);

    // -----------------------------------------------------------------------
    // WarrantVictimExtra — PCI's per-client dynamic "_U" columns for this
    // victim (WarrantVictimInterface.asp's "Additional Victim
    // Information" section).
    // -----------------------------------------------------------------------
    const [victimExtraFields, setVictimExtraFields] = useState([]);
    const [victimExtraFieldsReady, setVictimExtraFieldsReady] = useState(false);
    const victimExtraTypeMapRef = useRef({});
    const victimExtraIdRef = useRef(0);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/WarrantVictimExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
          const converted = convertApiFields(tableData, "WarrantVictimExtra");
          const typeMap = {};
          converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
          victimExtraTypeMapRef.current = typeMap;
          setVictimExtraFields(converted);
          setVictimExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error("[WarrantVictimForm] WarrantVictimExtra field defs fetch failed:", error);
        });
    }, []);

    // Fetches the saved extra row once a real warrantVictimID is present
    // (edit/view mode) — a brand new victim has no id yet.
    useEffect(() => {
      if (!victimExtraFieldsReady) return;
      const id = initialValues?.warrantVictimID;
      if (!isNonEmptyVariable(id)) {
        victimExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("WarrantVictimExtra", "warrantVictimID", id)
        .then((result) => {
          if (!result) return;
          victimExtraIdRef.current = result.raw?.WarrantVictimExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = victimExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          form.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error("[WarrantVictimForm] WarrantVictimExtra fetch failed:", error);
        });
    }, [initialValues?.warrantVictimID, victimExtraFieldsReady]);

    useEffect(() => {
      const bio = initialValues?.bio?.[0];
      if (bio) {
        form.setFieldsValue({
          lName: bio.lName,
          fName: bio.fName,
          mName: bio.mName,
          suffix: bio.suffix,
          daytimePhone: bio.daytimePhone,
          phone: bio.phone,
        });
        setAliases(bio.bioAliases || []);
        setAddresses(bio.bioAddresses || []);
      } else {
        form.resetFields();
        setAliases([]);
        setAddresses([]);
      }
    }, [initialValues, form]);

    useImperativeHandle(ref, () => ({
      validateAndGetPayload: () =>
        form.validateFields().then((values) => {
          // Split WarrantVictimExtra's dynamic fields out of the form
          // values — saved separately below, not folded into the bio-level
          // payload this modal hands back to InterestedParties.jsx.
          const victimExtraFieldKeys = victimExtraFields.map((f) => f.columnName);
          const victimExtraSnapshot = {};
          victimExtraFieldKeys.forEach((k) => {
            if (k in values) {
              const val = values[k];
              const htmlType = victimExtraTypeMapRef.current[k];
              victimExtraSnapshot[k] = dayjs.isDayjs(val)
                ? val.format(htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY")
                : val;
              delete values[k];
            }
          });

          const existingVictimId = initialValues?.warrantVictimID;
          if (victimExtraFieldKeys.length > 0 && isNonEmptyVariable(existingVictimId)) {
            saveExtraTableRecord({
              tableName: "WarrantVictimExtra",
              idColumn: "warrantVictimID",
              idValue: existingVictimId,
              extraRecord: {
                ...victimExtraSnapshot,
                warrantVictimExtraID: victimExtraIdRef.current || undefined,
              },
              extraIdKey: "warrantVictimExtraID",
            }).catch((error) => {
              console.error("[WarrantVictimForm] WarrantVictimExtra save failed:", error);
            });
          }

          return {
            warrantVictimID: initialValues?.warrantVictimID ?? 0,
            warrantID: initialValues?.warrantID ?? 0,
            bioID: initialValues?.bioID ?? initialValues?.bio?.[0]?.bioId ?? null,
            jacketID: initialValues?.jacketID ?? null,
            dateAdded: initialValues?.dateAdded ?? null,
            timeAdded: initialValues?.timeAdded ?? null,
            bio: [
              {
                ...(initialValues?.bio?.[0] || {}),
                lName: values.lName,
                fName: values.fName,
                mName: values.mName,
                suffix: values.suffix,
                daytimePhone: values.daytimePhone,
                phone: values.phone,
                bioAliases: aliases,
                bioAddresses: addresses,
              },
            ],
          };
        }),
    }));

    return (
      <FieldSecurityProvider
        entity="WarrantVictim"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={!disabled}
      >
        <StyledSectionHeading>
          <span>Personal Information</span>
        </StyledSectionHeading>

        <Form form={form} disabled={disabled}>
          <StyledGridRow gutter={16}>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="lName"
                formItemProps={{ name: "lName", label: "Last Name", rules: [{ required: true, message: "Last Name is required" }] }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="fName"
                formItemProps={{ name: "fName", label: "First Name" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="mName"
                formItemProps={{ name: "mName", label: "Middle Name" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="suffix"
                formItemProps={{ name: "suffix", label: "Suffix" }}
              >
                <Select options={getOptionsFromRefList("Suffix")} showSearch />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="daytimePhone"
                formItemProps={{ name: "daytimePhone", label: "Daytime Phone" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="phone"
                formItemProps={{ name: "phone", label: "Evening Phone" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          <AliasesList
            associates
            selectedAliases={aliases}
            onAliasesChange={setAliases}
            startingPoint={startingPoint}
          />

          <AddressesList
            associates
            selectedAddresses={addresses}
            onAddressesChange={setAddresses}
            startingPoint={startingPoint}
          />

          {victimExtraFields.length > 0 && (
            <>
              <StyledSectionHeading>
                <span>Additional Victim Information</span>
              </StyledSectionHeading>
              <StyledGridRow gutter={16}>
                {victimExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={!disabled}
                  />
                ))}
              </StyledGridRow>
            </>
          )}
        </Form>
      </FieldSecurityProvider>
    );
  },
);

WarrantVictimForm.displayName = "WarrantVictimForm";

export default WarrantVictimForm;
